import { DOC_PARAM, sharedDoc } from "@cuekards/core";

/**
 * A script in the page URL, so that a link is enough to hand somebody one.
 *
 * A tab already has a query string and a person already knows how to copy an
 * address, so the shareable thing the desktop app never had costs one
 * parameter. Both directions live here because they have to agree: the address
 * bar is written with something this same module would read back.
 *
 * Only a *reference* goes in the URL. A pasted script does not — it is somebody
 * else's document as often as not, it has no length limit worth relying on, and
 * the point of the paste source is that nothing leaves the tab.
 *
 * **The reading half moved into core with mobile-app ticket 15**, because a
 * phone reads the same rule and has no address bar to write one with. What is
 * left here is everything that needs a tab: the writer below, and the navigation
 * type the browser answers {@link isFreshArrival} from.
 */

export { DOC_PARAM };

/** What the page was asked to load, if anything — core's rule, handed this
 * tab's own query string. Kept as a name of its own because that is what the
 * page has in hand (`window.location.search`, leading `?` and all) and because
 * every caller and test here already says `preloadedDoc`. */
export function preloadedDoc(search: string): string | undefined {
  return sharedDoc(search);
}

/**
 * The address this page should be showing, given what is loaded.
 *
 * Passing `undefined` removes the parameter rather than leaving it: a stale
 * `doc` on a page showing a pasted script is worse than none, because copying
 * the address would hand somebody a different script from the one on screen.
 */
export function addressBarFor(href: string, doc: string | undefined): string {
  const url = new URL(href);
  if (doc === undefined) url.searchParams.delete(DOC_PARAM);
  else url.searchParams.set(DOC_PARAM, doc);
  return url.toString();
}

/**
 * Whether this page load is somebody *arriving*, as opposed to coming back.
 *
 * It decides what a `?doc=` means, and the two meanings want opposite things.
 * Following a link somebody shared should put you in front of the prompter with
 * nothing to click; reopening or reloading your own tab should not, because the
 * rule since web ticket 08 is that returning prefills rather than launching —
 * a full-screen prompter you did not ask for is not "picking up where I left
 * off" (story 15).
 *
 * Those two became indistinguishable the moment the page started writing the
 * loaded document into its own address bar, which is what makes this a
 * distinction that has to be drawn rather than a nicety. It is drawn from the
 * navigation type, which is the browser's own answer to the question: a fresh
 * navigation, a bookmark and a shared link are all `navigate`; a reload is
 * `reload` and a history move is `back_forward`. An absent entry is treated as
 * arrival, which is the harmless way round — the worst it does is show a
 * prompter to somebody whose browser did not say.
 */
export function isFreshArrival(navigationType: string | undefined): boolean {
  return navigationType === undefined || navigationType === "navigate";
}

/** How the page asks the browser what kind of load this is. */
export function currentNavigationType(): string | undefined {
  const [entry] = performance.getEntriesByType("navigation");
  return (entry as PerformanceNavigationTiming | undefined)?.type;
}
